import { useState } from "react";

import { Plus } from "phosphor-react";
import { RemovedMedication } from "@types-runway/removedMedication";

interface RemovedMedicationRowsProps {
  removedMedications: RemovedMedication[];
  /** Whether a save or payment is in progress. Buttons stay focusable but do nothing. */
  isBusy: boolean;
  restoringName: string | null;
  failedName: string | null;
  onRestore: (productName: string) => void;
}

export const describeRemovedMedication = ({ condition }: RemovedMedication) =>
  condition.suggestedMedication?.name ?? condition.product.name;

const listId = "removed-medications-list";

const RemovedMedicationList = ({
  removedMedications,
  isBusy,
  restoringName,
  failedName,
  onRestore,
}: RemovedMedicationRowsProps) => {
  // Local UI state: every mount starts collapsed.
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="removed-medications">
      <button
        type="button"
        className="removed-medications-toggle"
        aria-expanded={isExpanded}
        aria-controls={listId}
        onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
      >
        <span>{`Removed from your order (${removedMedications.length})`}</span>
        <span className="removed-medications-chevron" aria-hidden="true" />
      </button>
      <div id={listId} hidden={!isExpanded}>
        {removedMedications.map((removed) => {
          const productName = removed.condition.product.name;
          const medicationName = describeRemovedMedication(removed);
          const isRestoring = isBusy && restoringName === productName;
          return (
            <div className="removed-medication-row" key={productName}>
              <div className="removed-medication-line">
                <span className="removed-medication-name">{medicationName}</span>
                <button
                  type="button"
                  className="medication-action-button"
                  data-testid={`restore-medication-${productName}`}
                  data-restore-name={productName}
                  aria-label={`Add back ${medicationName} to your order`}
                  aria-disabled={isBusy}
                  aria-busy={isRestoring}
                  onClick={() => {
                    if (!isBusy) onRestore(productName);
                  }}
                >
                  <Plus aria-hidden="true" />
                </button>
              </div>
              {failedName === productName && (
                <p role="alert" className="remove-medication-error">
                  {`We couldn't add ${medicationName} back. Your order has not changed.`}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RemovedMedicationRows = (props: RemovedMedicationRowsProps) => {
  // The list unmounts at zero entries, so a later removal starts collapsed again.
  if (props.removedMedications.length === 0) return null;

  return <RemovedMedicationList {...props} />;
};
